if (!customElements.get('sticky-buy-bar')) {
  const OPTION_EVENTS = ['change', 'input', 'caliburn:color-swatch-change', 'caliburn:logo-color-change'];

  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-buy-button]');
      this.label = this.querySelector('[data-sticky-buy-label]');
      this.priceOutput = this.querySelector('[data-sticky-buy-price]');
      this.summary = this.querySelector('[data-sticky-buy-summary]');
      this.status = this.querySelector('[data-sticky-buy-status]');
      this.defaultLabel ??= this.label?.textContent.trim();

      // Resolve the product's own price/buy buttons while still inside its
      // details column (kept across the move to <body> below).
      if (!this.mainButton) {
        const details = this.closest('.product-details') || document;
        this.mainButton = details.querySelector('.buy-buttons-block button[name="add"]');
        this.buyBlock = this.mainButton?.closest('.buy-buttons-block');
        this.mainPrice = details.querySelector('product-price');
      }

      if (!this.mainButton || !this.buyBlock) return;

      // Move the bar to <body> so no transformed/overflow ancestor can
      // clip or re-anchor its fixed positioning. This reconnects the
      // element, which runs the rest of setup.
      if (this.parentElement !== document.body) {
        // A section re-render (e.g. on variant change) creates a fresh bar;
        // replace any earlier one rather than stacking them.
        document.querySelectorAll('body > sticky-buy-bar').forEach((bar) => bar.remove());
        document.body.appendChild(this);
        return;
      }

      if (this.listening) return;

      // Measured on scroll: simpler and more reliable here than
      // IntersectionObserver margins, since the sticky header's height
      // changes as the announcement bar scrolls away.
      this.header = document.querySelector('#header-component');
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      // Capture phase: some layouts scroll an inner wrapper, not the window.
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onScroll);
      this.update();
      this.listening = true;

      // Keep the price in step with variant changes (the main price block
      // re-renders its contents on each change).
      if (this.mainPrice) {
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
        this.syncPrice();
      }

      if (this.button) this.button.onclick = () => this.buy();

      // After an add, the button becomes "Go to Cart". Changing any option
      // on the page means a new build, so switch back to adding.
      this.onOptionChange = (event) => {
        if (this.contains(event.target)) return;
        if (!event.target.closest?.('.product-details')) return;
        if (this.dataset.state === 'added' || this.dataset.state === 'cart') this.setState('idle');
      };
      // Swatch pickers report their own events rather than change/input.
      OPTION_EVENTS.forEach((type) => document.addEventListener(type, this.onOptionChange));

      // Any add from this product's form, whether from the bar or the main
      // button, turns the bar into "Go to Cart". The theme's add-to-cart
      // runs off screen (and the cart drawer doesn't auto-open on this
      // store), so the bar is where the result shows.
      this.onCartUpdate = (event) => {
        const action = event.action ?? event.detail?.action;
        if (action && action !== 'add') return;
        if (!event.target.closest?.('.product-details')) return;
        clearTimeout(this.fallbackTimer);
        const promise = event.promise || event.detail?.promise;
        if (!promise) return this.setState('added');
        promise
          .then((result) => this.setState(result?.detail?.didError ? 'error' : 'added'))
          .catch(() => this.setState('error'));
      };
      document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      OPTION_EVENTS.forEach((type) => document.removeEventListener(type, this.onOptionChange));
      document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
      window.removeEventListener('resize', this.onScroll);
      this.priceObserver?.disconnect();
      this.priceObserver = null;
      this.listening = false;
    }

    // Visible while neither the main price nor the main buy buttons are on
    // screen (content under a sticky header doesn't count), all the way down
    // through the footer. Hysteresis around the buy buttons: hide as soon as
    // they reach the screen, but only show again once they're 40px clear of
    // it, so small scroll movements near the edge can't make the bar flicker.
    update() {
      const top = Math.max(this.header?.getBoundingClientRect().bottom || 0, 0);
      const bottom = window.innerHeight;
      const priceRect = this.mainPrice?.getBoundingClientRect();
      const priceShown = priceRect
        ? Math.min(priceRect.bottom, bottom) - Math.max(priceRect.top, top) >= priceRect.height / 2
        : false;

      const buyRect = this.buyBlock.getBoundingClientRect();
      const shown = this.dataset.visible === 'true';
      const clearance = shown ? 0 : 40;
      const buyOffScreen = buyRect.top > bottom + clearance || buyRect.bottom < top - clearance;

      const show = !priceShown && buyOffScreen;
      if (show) this.renderSummary();
      if (show !== shown) this.dataset.visible = String(show);
    }

    // Desktop-only line under the product name: current length and colors.
    renderSummary() {
      if (!this.summary || window.innerWidth < 750) return;
      const value = (name) => document.querySelector(`[name="${CSS.escape(name)}"]`)?.value;
      const length = document.querySelector('.product-details variant-picker input:checked')?.value;
      const weight = document.querySelector('[name="properties[Weight]"]:checked')?.value;
      const parts = [
        length && `Length ${length}`,
        weight && `${weight} weight`,
        value('properties[Barrel Color]') && `${value('properties[Barrel Color]')} barrel`,
        value('properties[Handle Color]') && `${value('properties[Handle Color]')} handle`,
        value('properties[Logo Color]') && `${value('properties[Logo Color]')} logo`,
      ].filter(Boolean);
      this.summary.textContent = parts.join('  ·  ');
    }

    syncPrice() {
      const price = this.mainPrice?.querySelector('.price')?.textContent.trim();
      if (price && this.priceOutput) this.priceOutput.textContent = price;
    }

    buy() {
      if (this.dataset.state === 'adding') return;
      if (this.dataset.state === 'added' || this.dataset.state === 'cart') {
        window.location.href = window.Theme?.routes?.cart_url || '/cart';
        return;
      }

      // Re-resolve in case a variant change re-rendered the buy buttons.
      const details = document.querySelector('.product-details');
      const mainButton = details?.querySelector('.buy-buttons-block button[name="add"]') || this.mainButton;
      if (!mainButton?.isConnected) return;
      this.mainButton = mainButton;
      this.buyBlock = mainButton.closest('.buy-buttons-block') || this.buyBlock;

      // No purchasable variant selected: send them to the variant picker.
      if (mainButton.disabled) {
        const picker = details?.querySelector('variant-picker') || this.buyBlock;
        this.highlight(picker);
        return;
      }

      const form = mainButton.form;
      const invalid = form && Array.from(form.elements).find((el) => el.willValidate && !el.checkValidity());
      if (invalid) {
        this.highlight(invalid.closest('.shopify-block') || invalid, invalid);
        return;
      }

      // onCartUpdate picks up the result. If the theme never reports back,
      // don't leave the button stuck.
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = setTimeout(() => this.setState('idle'), 8000);

      this.setState('adding');
      mainButton.click();
    }

    setState(state) {
      clearTimeout(this.resetTimer);
      this.dataset.state = state;
      const text = {
        adding: 'Adding...',
        added: '✓ Added',
        cart: 'Go to Cart',
        error: "Couldn't add. Try again",
      }[state];
      if (this.label) this.label.textContent = text || this.defaultLabel;
      if (this.status) this.status.textContent = state === 'added' ? 'Added to cart' : state === 'error' ? text : '';
      // Brief confirmation, then a standing "Go to Cart" until options change.
      if (state === 'added') this.resetTimer = setTimeout(() => this.setState('cart'), 1200);
      if (state === 'error') this.resetTimer = setTimeout(() => this.setState('idle'), 2500);
    }

    highlight(target, field) {
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.remove('sticky-buy-bar-highlight');
      // Restart the animation if it was already running.
      void target.offsetWidth;
      target.classList.add('sticky-buy-bar-highlight');
      setTimeout(() => target.classList.remove('sticky-buy-bar-highlight'), 1900);
      if (field) {
        field.focus({ preventScroll: true });
        field.reportValidity?.();
      }
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
}
